import { ConversationLifecycleService } from '@klant/conversational-connector/services/conversation-life-cycle-service/conversation-life-cycle.service';
import { InactivityService } from '@klant/conversational-connector/services/inactivity-service/inactivity.service';
import { MessageHandlerService } from '@klant/conversational-connector/services/message-handle-service/message-handler.service';
import { ScreenreaderService } from '@klant/conversational-connector/services/screenreader/screenreader.service';
import { UnreadMessageService } from '@klant/conversational-connector/services/unread-message-service/unread-message.service';
import { WebsocketService } from '@klant/conversational-connector/services/wsservice/websocket.service';

import { afterRenderEffect, ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';

import { ApWindowService } from '@ap/cdk';
import { ApContentService } from '@ap/content';
import { DEFAULT_BREAKPOINT_SMALL } from '@ap/style';

import { SessionStorageService } from '../../session-storage/session-storage.service';
import { ChatUIStore } from '../../signal-stores/chat-ui-store/chat-ui.store';
import { ConversationalChatStore } from '../../signal-stores/conversational-chat-store/conversational-chat.store';
import { ConversationalConfigStore } from '../../signal-stores/conversational-config-store/conversational-config.store';

import content from './klant-conversational-connector.content.json';
import { ActionModel, ConversationalChatVisibility } from './klant-conversational-connector.model';

@Component({
    selector: 'klant-conversational-connector',
    templateUrl: './klant-conversational-connector.component.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        '(window:resize)': 'updateOverflowSetting()'
    }
})
export class KlantConversationalConnectorComponent {
    private readonly apContentService = inject(ApContentService);
    private readonly chatUIStore = inject(ChatUIStore);

    private readonly apWindowService = inject(ApWindowService);
    private readonly conversationalChatStore = inject(ConversationalChatStore);
    private readonly conversationalConfigStore = inject(ConversationalConfigStore);
    private readonly conversationLifecycleService = inject(ConversationLifecycleService);
    private readonly inactivityService = inject(InactivityService);
    private readonly messageHandlerService = inject(MessageHandlerService);
    private readonly screenreaderService = inject(ScreenreaderService);
    private readonly sessionStorageService = inject(SessionStorageService);
    private readonly unreadMessageService = inject(UnreadMessageService);
    private readonly webSocketService = inject(WebsocketService);

    public readonly idleTimerActive = this.conversationalChatStore.idleTimerActive;
    public readonly showChat = this.conversationalChatStore.showChat;
    public readonly showTakeoverError = computed(
        () => this.conversationalConfigStore.doneLoading() && this.conversationalConfigStore.errorGettingConfiguration() === true
    );
    public readonly showConnectionError = this.conversationalChatStore.showConnectionError;
    public readonly showLiveChatFaqPopUp = this.conversationalChatStore.showLiveChatFaqPopUp;
    public readonly showRetryConnectionError = this.conversationalChatStore.showRetryConnectionError;
    public readonly takeoverError = this.conversationalChatStore.takeoverError;
    public readonly showSkeletonLoader = computed(
        () => !this.showChat() && !this.showConnectionError() && !this.showRetryConnectionError() && !this.showTakeoverError()
    );

    public readonly liveMessage1 = this.screenreaderService.liveMessage1;
    public readonly liveMessage2 = this.screenreaderService.liveMessage2;

    public constructor() {
        this.apContentService.addContentItems(content);

        effect(() => {
            this.markMessagesReadOnChatOpen();
        });

        effect(() => {
            this.updateOverflowSetting();
        });

        afterRenderEffect({
            write: () => {
                this.handleOverflowSettingChange();
            }
        });
    }

    public updateOverflowSetting(): void {
        const chatVisibility = this.conversationalChatStore.chatVisibility();
        const innerWidth = this.apWindowService.getWindow().innerWidth;
        if (chatVisibility === ConversationalChatVisibility.OPEN && innerWidth <= DEFAULT_BREAKPOINT_SMALL.max) {
            this.chatUIStore.setOverflowEnabled(false);
            return;
        }
        this.chatUIStore.setOverflowEnabled(true);
    }

    public onSendMessage(text: string): void {
        this.messageHandlerService.sendMessage(text);
    }

    public onSendAction(actionModel: ActionModel): void {
        this.messageHandlerService.sendAction(actionModel);
    }

    public refreshChatComponent(): void {
        this.conversationalChatStore.resetConversation();
        this.conversationalChatStore.clearErrors();
        this.sessionStorageService.updateConversationSessionDetails(undefined);
        this.conversationLifecycleService.createConversation();
    }

    public promptContinueConversation(): void {
        this.inactivityService.promptContinue();
    }

    public promptEndConversation(): void {
        this.inactivityService.promptEnd();
    }

    public hideLiveChatFaqPopUp(): void {
        this.conversationalChatStore.setShowLiveChatFaqPopUp(false);
    }

    private markMessagesReadOnChatOpen() {
        const visibility = this.conversationalChatStore.chatVisibility();
        if (this.webSocketService.subject && visibility === ConversationalChatVisibility.OPEN) {
            this.unreadMessageService.markMessagesAsRead(this.conversationalChatStore.unreadChatMessages());
        }
    }

    private handleOverflowSettingChange(): void {
        const setOverflowYToElement = (setting: 'auto' | 'hidden', element: HTMLElement) => {
            element.style.overflowY = setting;
        };

        const htmlElement = this.apWindowService.getWindow().document.querySelector('html') as HTMLElement;
        if (!htmlElement) {
            return;
        }

        const overflowEnabled = this.chatUIStore.overflowEnabled();
        setOverflowYToElement(overflowEnabled ? 'auto' : 'hidden', htmlElement);
    }
}
